// ADAPTED from ptc-core web/src/api/feedback.ts @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source:
//   - `app` is 'statetax' (contract §3);
//   - the request goes to the CRM API (contract §2), not this app's own API, with a token for the CRM's scope
//     acquired silently on statetax's existing MSAL instance (contract §3). Core's `useApi` client doesn't exist
//     here, so the POST and the 400 → field-errors mapping (core's ApiValidationError) are inlined;
//   - the admin list route (`useFeedbackList`) isn't copied: the reports are read on the CRM's admin Feedback page.
// Conformance: src/components/feedback/FeedbackWidgetConformance.test.tsx.
//
// POST /v1/feedback — any staff member. 201 means the report is PERSISTED (status `received`); filing it as a
// GitHub issue is a later, separate step. There is deliberately no reporter, status or GitHub field on the
// create body: the server takes identity from the token and status from the return path.
//
// 🔴 Precondition only Christian can meet (contract §3): statetax's app registration
// (d81abc94-cad6-48a3-ae94-eba6f200ca18) must be granted CRM_FEEDBACK_SCOPE. Until then acquireTokenSilent fails
// (consent_required / invalid_grant) and the dialog shows its send-failed error: never a fake reference.

import { useMutation } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import type { FeedbackContext } from '../feedback/feedbackContext';

export const CRM_FEEDBACK_URL = 'https://ptc-core-crm-api.azurewebsites.net/api/v1/feedback';
export const CRM_FEEDBACK_SCOPE = 'api://d80aa38e-6b76-4cf9-96c8-985e718a3a57/access_as_user';

export type FeedbackKind = 'problem' | 'improvement';

export interface CreateFeedbackBody {
  kind: FeedbackKind;
  text: string;
  expected?: string;
  creditByName: boolean;
  app: 'statetax';
  context: FeedbackContext;
}

export interface FeedbackCreated {
  id: string;
  /** `FB-1234` — what the reporter is shown. */
  reference: string;
  status: string;
  createdAt: string;
}

/** Field-level validation errors from a 400 — shape: `{ errors: { field: [msgs] } }` (core's ApiValidationError). */
export type FieldErrors = Record<string, string[]>;

export class FeedbackValidationError extends Error {
  constructor(public readonly errors: FieldErrors) {
    super('feedback validation failed');
    this.name = 'FeedbackValidationError';
  }
  /** First message for a field, if any — convenient for inline display. */
  firstFor(field: string): string | undefined {
    return this.errors[field]?.[0];
  }
}

export function useCreateFeedback() {
  const { instance, accounts } = useMsal();
  return useMutation({
    mutationFn: async (body: CreateFeedbackBody): Promise<FeedbackCreated> => {
      const account = instance.getActiveAccount() ?? accounts[0];
      const { accessToken } = await instance.acquireTokenSilent({ scopes: [CRM_FEEDBACK_SCOPE], account });
      const resp = await fetch(CRM_FEEDBACK_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.status === 400) {
        const parsed = (await resp.json().catch(() => null)) as { errors?: FieldErrors } | null;
        if (parsed?.errors) throw new FeedbackValidationError(parsed.errors);
      }
      if (!resp.ok) throw new Error(`feedback ${resp.status}`);
      return (await resp.json()) as FeedbackCreated;
    },
  });
}
